function parseAssetConfig(asset) {
  if (!asset?.config) return null;
  try {
    return typeof asset.config === 'string' ? JSON.parse(asset.config) : asset.config;
  } catch {
    return null;
  }
}

/**
 * Format Unity PascalCase class names for display (ItemPlaceable -> Item Placeable).
 * @param {string} className
 * @returns {string}
 */
export const formatUnityClassName = (className) => {
  if (!className) return '';
  return String(className)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
};

function isBlueprintAsset(asset, config) {
  if (asset?.inventoryType === 'blueprints') return true;
  const type = asset?.type || asset?.assetType || '';
  if (type.toLowerCase() === 'blueprint') return true;
  if (config?.inventoryType === 'blueprints') return true;
  if (config?.type?.toLowerCase() === 'blueprint') return true;
  return false;
}

/**
 * Unity-like asset class label from server metadata (type column, assetType, config.type).
 * @param {Object} asset - The asset object from Assets API
 * @returns {string} - e.g. "Item Placeable", "Decoration", "Blueprint"
 */
export const getUnityTypeLabel = (asset) => {
  if (!asset) return 'Item';

  const config = parseAssetConfig(asset);
  if (isBlueprintAsset(asset, config)) {
    return 'Blueprint';
  }

  const unityClass = asset.type;
  if (unityClass && unityClass !== 'Item') {
    return formatUnityClassName(unityClass);
  }

  const subType = asset.assetType || config?.type;
  if (subType && String(subType).toLowerCase() !== 'item') {
    return formatUnityClassName(subType);
  }

  if (unityClass) {
    return formatUnityClassName(unityClass);
  }

  return 'Item';
};

/**
 * @deprecated Prefer getUnityTypeLabel — kept for existing call sites.
 * @param {Object} asset
 * @returns {string}
 */
export const getAssetTypeLabel = (asset) => getUnityTypeLabel(asset);

const normalizeTypeLabel = (label) => String(label || '').trim().toLowerCase();

/**
 * Subtle badge styles per Unity type display label (dark-theme friendly).
 * Uses MUI palette tokens where possible; custom rgba for purple/orange variants.
 */
const UNITY_TYPE_BADGE_SCHEMES = {
  'item placeable': {
    color: 'success',
    sx: { bgcolor: 'success.dark', color: 'success.light', border: '1px solid', borderColor: 'success.main' },
  },
  decoration: {
    sx: { bgcolor: 'rgba(156, 39, 176, 0.28)', color: '#e1bee7', border: '1px solid rgba(206, 147, 216, 0.45)' },
  },
  'item tool': {
    color: 'info',
    sx: { bgcolor: 'info.dark', color: 'info.light', border: '1px solid', borderColor: 'info.main' },
  },
  blueprint: {
    color: 'warning',
    sx: { bgcolor: 'warning.dark', color: 'warning.light', border: '1px solid', borderColor: 'warning.main' },
  },
  food: {
    sx: { bgcolor: 'rgba(255, 152, 0, 0.28)', color: '#ffcc80', border: '1px solid rgba(255, 183, 77, 0.45)' },
  },
  'item consumable': {
    color: 'error',
    sx: { bgcolor: 'error.dark', color: 'error.light', border: '1px solid', borderColor: 'error.main' },
  },
  'item wearable': {
    color: 'secondary',
    sx: { bgcolor: 'secondary.dark', color: 'secondary.light', border: '1px solid', borderColor: 'secondary.main' },
  },
  clothes: {
    color: 'secondary',
    sx: { bgcolor: 'secondary.dark', color: 'secondary.light', border: '1px solid', borderColor: 'secondary.main' },
  },
  building: {
    color: 'primary',
    sx: { bgcolor: 'primary.dark', color: 'primary.light', border: '1px solid', borderColor: 'primary.main' },
  },
  'crafting material': {
    color: 'info',
    sx: { bgcolor: 'rgba(33, 150, 243, 0.22)', color: '#90caf9', border: '1px solid rgba(144, 202, 249, 0.35)' },
  },
  quest: {
    color: 'error',
    sx: { bgcolor: 'rgba(255, 82, 82, 0.22)', color: '#ef9a9a', border: '1px solid rgba(239, 154, 154, 0.35)' },
  },
  minting: {
    color: 'primary',
    sx: { bgcolor: 'rgba(0, 229, 255, 0.18)', color: '#80deea', border: '1px solid rgba(128, 222, 234, 0.35)' },
  },
  item: {
    color: 'default',
    sx: { bgcolor: 'action.selected', color: 'text.secondary', border: '1px solid', borderColor: 'divider' },
  },
};

const DEFAULT_TYPE_BADGE_SCHEME = {
  color: 'default',
  sx: { bgcolor: 'action.selected', color: 'text.secondary', border: '1px solid', borderColor: 'divider' },
};

/**
 * MUI Chip `color` prop for a Unity type display label.
 * @param {Object|string} assetOrLabel
 * @returns {'default'|'primary'|'secondary'|'error'|'info'|'success'|'warning'|undefined}
 */
export const getUnityTypeMuiColor = (assetOrLabel) => {
  const label = typeof assetOrLabel === 'string'
    ? assetOrLabel
    : getUnityTypeLabel(assetOrLabel);
  return UNITY_TYPE_BADGE_SCHEMES[normalizeTypeLabel(label)]?.color;
};

/**
 * Props for a subtle type badge Chip (label, color, sx).
 * @param {Object|string} assetOrLabel
 * @returns {{ label: string, size: 'small', variant: 'filled', color?: string, sx: object }}
 */
export const getUnityTypeChipProps = (assetOrLabel) => {
  const label = typeof assetOrLabel === 'string'
    ? assetOrLabel
    : getUnityTypeLabel(assetOrLabel);
  const scheme = UNITY_TYPE_BADGE_SCHEMES[normalizeTypeLabel(label)] || DEFAULT_TYPE_BADGE_SCHEME;
  return {
    label,
    size: 'small',
    variant: 'filled',
    ...(scheme.color ? { color: scheme.color } : {}),
    sx: { height: 20, fontSize: '0.7rem', fontWeight: 600, ...scheme.sx },
  };
};

/**
 * Dropdown label: "Neon Up Arrow — Item Placeable"
 * @param {Object} asset
 * @returns {string}
 */
export const getAssetPickerLabel = (asset) => {
  if (!asset) return '';
  return `${asset.title || asset.id} — ${getUnityTypeLabel(asset)}`;
};

/**
 * Formatted label with parentheses for compact display.
 * @param {Object} asset
 * @returns {string}
 */
export const getAssetLabel = (asset) => {
  if (!asset) return '';
  return `${asset.title || asset.id} (${getUnityTypeLabel(asset)})`;
};

/** Subtle helper copy for trader deal sold/cost asset pickers. */
export const TRADER_ASSET_PICKER_HELPER =
  'Grid object prefabs are hidden; select inventory items only.';

/**
 * Whether an asset can be picked for trader deal sold/cost fields.
 * GridObjectAsset world prefabs are excluded; Items, blueprints, and other types remain selectable.
 *
 * @param {Object|null|undefined} asset
 * @returns {boolean}
 */
export const isSelectableTraderAsset = (asset) => {
  if (!asset?.id) return false;
  const unityClass = String(asset.type || '').trim();
  return unityClass.toLowerCase() !== 'gridobjectasset';
};

/**
 * @param {Object[]} assets
 * @returns {Object[]}
 */
export const filterSelectableTraderAssets = (assets) => (
  (assets || []).filter((asset) => isSelectableTraderAsset(asset))
);

/**
 * Autocomplete filter: title, id, Unity class, config subtype, inventoryType.
 * @param {Object[]} options
 * @param {{ inputValue?: string }} state
 * @returns {Object[]}
 */
export const filterAssetOptions = (options, { inputValue }) => {
  if (!inputValue?.trim()) return options.slice(0, 100);

  const searchTerm = inputValue.trim().toLowerCase();
  return options.filter((option) => {
    const config = parseAssetConfig(option);
    const fields = [
      option.title,
      option.id,
      option.type,
      option.assetType,
      option.inventoryType,
      config?.type,
    ];
    return fields.some((field) => field && String(field).toLowerCase().includes(searchTerm));
  });
};

/**
 * Helper function to truncate GUID for display
 * @param {string} guid - The full GUID
 * @returns {string} - Truncated GUID like "1234-...-bcde"
 */
export const truncateGuid = (guid) => {
  if (!guid || guid.length <= 13) return guid;
  return `${guid.substring(0, 4)}-...-${guid.substring(guid.length - 4)}`;
};
